/**
 * Activity Log Service
 *
 * Logs platform activities for superadmin audit. All activities are stored in
 * Firebase Realtime Database at admin/activityLog.
 */

import { ref, push, set, get, query, orderByChild, limitToLast, endBefore } from "firebase/database";
import { db } from "../auth/firebase";

const LOG_PATH = "admin/activityLog";
const MAX_FETCH = 2000;

/**
 * Activity types for filtering
 */
export const ACTIVITY_TYPES = {
  USER_CREATED: "user_created",
  USER_UPDATED: "user_updated",
  USER_BECAME_WRITER: "user_became_writer",
  CONTENT_CREATED: "content_created",
  CONTENT_UPDATED: "content_updated",
  CONTENT_DELETED: "content_deleted",
  LIKE_ADDED: "like_added",
  LIKE_REMOVED: "like_removed",
  COMMENT_ADDED: "comment_added",
  BOOKMARK_ADDED: "bookmark_added",
  BOOKMARK_REMOVED: "bookmark_removed",
  CONTENT_MODERATED: "content_moderated",
};

/**
 * Push an activity entry (non-blocking; errors are logged only).
 * @param {Object} activity - { type, userId, userEmail, userName, ...meta }
 */
export const pushActivity = async (activity) => {
  if (!activity || !activity.type) return;
  try {
    const logRef = ref(db, LOG_PATH);
    const newRef = push(logRef);
    await set(newRef, {
      ...activity,
      createdAt: new Date().toISOString(),
    });
  } catch (error) {
    console.warn("[activityLog] push failed:", error?.message || error);
  }
};

function applyActivityFilters(list, { type, dateFrom, dateTo, userId } = {}) {
  let filtered = list;
  if (type) {
    filtered = filtered.filter((a) => a.type === type);
  }
  if (userId) {
    const uid = String(userId).toLowerCase();
    filtered = filtered.filter(
      (a) =>
        (a.userId && String(a.userId).toLowerCase() === uid) ||
        (a.userEmail && String(a.userEmail).toLowerCase().includes(uid)) ||
        (a.userName && String(a.userName).toLowerCase().includes(uid))
    );
  }
  if (dateFrom) {
    const from = new Date(dateFrom);
    filtered = filtered.filter((a) => new Date(a.createdAt) >= from);
  }
  if (dateTo) {
    const to = new Date(dateTo);
    to.setHours(23, 59, 59, 999);
    filtered = filtered.filter((a) => new Date(a.createdAt) <= to);
  }
  return filtered;
}

/**
 * Get activity logs with optional filters (loads up to MAX_FETCH, filters, then limits).
 * @param {Object} options - { type?, dateFrom?, dateTo?, userId?, limit? }
 * @returns {Promise<Array<{ id, ...activity }>>}
 */
export const getActivities = async (options = {}) => {
  const { type, dateFrom, dateTo, userId, limit = 500 } = options;
  try {
    const logRef = ref(db, LOG_PATH);
    const snapshot = await get(logRef);
    if (!snapshot.exists()) return [];

    const list = [];
    snapshot.forEach((child) => {
      const val = child.val();
      if (!val || !val.createdAt) return;
      list.push({ id: child.key, ...val });
    });

    list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    let filtered = applyActivityFilters(list, { type, dateFrom, dateTo, userId });
    return filtered.slice(0, Math.min(limit, MAX_FETCH));
  } catch (error) {
    console.error("[activityLog] getActivities failed:", error);
    throw error;
  }
};

/**
 * Server-side paginated activity log (newest first) when no filters are applied.
 * Uses RTDB queries on `createdAt`.
 *
 * When filters are set, loads up to MAX_FETCH, applies filters, then slices by `first` / `rows`.
 *
 * @param {Object} options
 * @param {number} [options.pageSize=20]
 * @param {{ oldestCreatedAt: string }|null} [options.cursor] - Oldest `createdAt` on the previous page (for next page)
 * @param {string} [options.type]
 * @param {string} [options.dateFrom]
 * @param {string} [options.dateTo]
 * @param {string} [options.userId]
 * @param {number} [options.first=0] - Offset when using filters (lazy table `first`)
 * @param {number} [options.rows=20] - Page size when using filters
 * @returns {Promise<{ items: Array, hasMore: boolean, nextCursor: { oldestCreatedAt: string }|null, total: number|null }>}
 */
export const getActivitiesPaged = async (options = {}) => {
  const {
    pageSize = 20,
    cursor = null,
    type,
    dateFrom,
    dateTo,
    userId,
    first = 0,
    rows = pageSize,
  } = options;
  const hasFilters = !!(type || dateFrom || dateTo || userId);

  if (hasFilters) {
    const all = await getActivities({
      type,
      dateFrom,
      dateTo,
      userId,
      limit: MAX_FETCH,
    });
    const total = all.length;
    const slice = all.slice(first, first + rows);
    const hasMore = first + rows < total;
    return { items: slice, hasMore, nextCursor: null, total };
  }

  try {
    const logRef = ref(db, LOG_PATH);
    const take = Math.min(Math.max(1, rows), 200);
    const limit = take + 1;
    const q = cursor?.oldestCreatedAt
      ? query(logRef, orderByChild("createdAt"), endBefore(cursor.oldestCreatedAt), limitToLast(limit))
      : query(logRef, orderByChild("createdAt"), limitToLast(limit));

    const snapshot = await get(q);
    const batch = [];
    snapshot.forEach((child) => {
      const val = child.val();
      if (!val || !val.createdAt) return;
      batch.push({ id: child.key, ...val });
    });
    batch.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    const hasMore = batch.length > take;
    const items = hasMore ? batch.slice(0, take) : batch;
    const nextCursor =
      hasMore && items.length > 0 ? { oldestCreatedAt: items[items.length - 1].createdAt } : null;
    return { items, hasMore, nextCursor, total: null };
  } catch (error) {
    console.error("[activityLog] getActivitiesPaged failed:", error);
    throw error;
  }
};
